import { getFormValues } from '../common/form.es6';
import MaEvent from '../common/ma-event';
import { openModal } from '../common/popin.es6';
import HTTPService from '../common/services/HTTPService.es6';
import { bindForm } from '../common/tagging';
import { displayErrorToaster } from '../common/toaster.es6';

/**
 * Realtor message field name regex.
 * @type {RegExp}
 */
const REALTOR_FIELD = /^realtor_message_(.*?)_(.*?)$/gim;

/**
 * Sale status field name regex.
 * @type {RegExp}
 */
const SALE_STATUS_FIELD = /^sale_status_(.*?)$/gim;

/**
 * CSS class for error text.
 * @type {String}
 */
const errorTextClass = 'field__errors';

/**
 * Handle click on link for showing comment.
 * @param  {DomNode} linkNode Link
 */
function handleCommentToggle(linkNode) {
    if (!linkNode) {
        return;
    }

    const textarea = document.getElementById(linkNode.dataset.commentId);
    if (textarea) {
        textarea.classList.add('followup__comment--active');
        textarea.focus();
    }

    linkNode.parentNode.removeChild(linkNode);
}

/**
 * Retrieve of create feedback from apiValues
 * @param  {Object} feedbacks Realtor feedbacks
 * @param  {String} id        Identifier of realtor message
 * @return {Object}           Realtor feedback
 */
function getOrCreateFeedback(feedbacks, id) {
    const matches = feedbacks.filter(feedback => feedback.realtor_message.id === id);

    let feedback;
    if (matches.length > 0) {
        [feedback] = matches;
    } else {
        feedback = {
            realtor_message: {
                id,
            },
        };

        feedbacks.push(feedback);
    }

    return feedback;
}

/**
 * Compute feedbacks data
 * @param  {Object} values Form values
 * @return {Object}        API format
 */
function getFeedbacks(values) {
    const feedbacks = [];

    if (values) {
        Object.keys(values).forEach(name => {
            String(name).replace(REALTOR_FIELD, (all, id, field) => {
                const feedback = getOrCreateFeedback(feedbacks, Number(id));

                if (values[name]) {
                    switch (field) {
                        case 'comment':
                            feedback.comment = values[all];
                            break;
                        case 'feedback':
                            feedback.feedback = values[all];
                            break;
                        case 'sale_started':
                            feedback.sale_started = values[all] === 'on';
                            break;
                        default:
                            window.console.warn(`Unknown field name ${field}`);
                    }
                }
            });
        });
    }

    return feedbacks;
}

/**
 * Compute sale status data
 * @param  {Object} values Form values
 * @return {Object}        API format
 */
function getSaleStatus(values) {
    const saleStatus = {};

    if (values) {
        Object.keys(values).forEach(name => {
            String(name).replace(SALE_STATUS_FIELD, (all, field) => {
                saleStatus[field] = values[all] === 'on';
            });
        });
    }

    return saleStatus;
}

/**
 * Compute MeilleursAgents data
 * @param  {Object} values Form values
 * @return {Object}        API format
 */
function getMeilleursAgentsFeedback(values) {
    const maFeedback = {
        feedback: values.satisfaction,
    };

    if (values.satifaction_comment) {
        maFeedback.comment = values.satifaction_comment;
    }

    return maFeedback;
}

/**
 * Convert form to API format
 * @param  {Object} values Form values
 * @return {Object}        API format
 */
function formToApi(values) {
    const apiValues = {
        [MA.Context.feedbacksName]: getFeedbacks(values),
        meilleursagents_feedback: getMeilleursAgentsFeedback(values),
    };

    if (MA.Context.hasSaleStatus) {
        apiValues.sale_status = getSaleStatus(values);
    }

    return apiValues;
}

/**
 * Send values to API and ignoring errors
 * @param  {Object} values Values of form
 */
function sendValues(values) {
    HTTPService.post(MA.Context.validate_url, values).catch(e => window.console.error(e));
}

/**
 * Clear all error in DOM.
 */
function clearErrors() {
    const errors = document.querySelectorAll(`.${errorTextClass}`);
    for (let i = 0, size = errors.length; i < size; ++i) {
        const error = errors[i];
        error.parentNode.removeChild(error);
    }

    const errorsWrapper = document.querySelectorAll('.field--invalid,.radio-list--invalid');
    for (let i = 0, size = errorsWrapper.length; i < size; ++i) {
        errorsWrapper[i].classList.remove('field--invalid');
        errorsWrapper[i].classList.remove('radio-list--invalid');
    }
}

/**
 * Scroll to first error found in dom.
 */
function scrollToFirstError() {
    const errorField = document.querySelector(`.${errorTextClass}`);
    if (errorField) {
        if ('scrollBehavior' in document.documentElement.style) {
            errorField.scrollIntoView({
                behavior: 'smooth',
            });
        } else {
            errorField.scrollIntoView();
        }
    }
}

/**
 * Handle feedback errors.
 * @param  {Object} errors Realtors error indexed by they order in HTML
 */
function handleFeedbacksErrors(errors) {
    if (!errors) {
        return;
    }

    Object.keys(errors).forEach(index => {
        const fieldWrapper = document.querySelector(`.field[data-index-id="${index}"]`);

        if (fieldWrapper) {
            const error = document.createElement('p');
            error.classList.add(errorTextClass);
            error.innerText = errors[index].feedback.join(', ');

            fieldWrapper.classList.add('field--invalid');
            fieldWrapper.querySelector('.field__control-wrapper').appendChild(error);
        }
    });
}

/**
 * Handle MeilleursAgents feedback errors.
 * @param  {Object} errors Feedback error
 */
function handleMeilleursAgentsFeedbackErrors(errors) {
    if (!errors) {
        return;
    }

    const fieldWrapper = document.querySelector('[data-ma-feedback]');

    if (fieldWrapper) {
        const error = document.createElement('p');
        error.classList.add(errorTextClass);
        error.innerText = errors.feedback.join(', ');

        const radioList = fieldWrapper.querySelector('.radio-list');
        if (radioList) {
            radioList.classList.add('radio-list--invalid');
        }

        fieldWrapper.appendChild(error);
    }
}

/**
 * Get submit function
 * @param  {DOMNode} form         Form
 * @param  {DOMNode} submitButton Submit button
 * @return {Function}             Click handler
 */
function getSubmitFn(form, submitButton) {
    return event => {
        event.preventDefault();

        if (submitButton.disabled) {
            return;
        }

        // eslint-disable-next-line no-param-reassign
        submitButton.disabled = true;

        const $form = $(form);
        $form.trigger(MaEvent.formSubmitAttempt);
        clearErrors();

        const method = String(form.getAttribute('method')).toLowerCase();
        const url = String(form.getAttribute('action'));

        HTTPService[method](url, formToApi(getFormValues(form)))
            .then(() => {
                $form.trigger(MaEvent.formSubmitSuccess);
                openModal('[data-followup-success-popin]');
            })
            .catch(error => {
                $form.trigger(MaEvent.formSubmitError);
                // eslint-disable-next-line no-param-reassign
                submitButton.disabled = false;

                if (
                    error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.messages
                ) {
                    const { messages } = error.response.data;
                    handleFeedbacksErrors(messages[MA.Context.feedbacksName]);
                    handleMeilleursAgentsFeedbackErrors(messages.meilleursagents_feedback);

                    scrollToFirstError();
                } else {
                    displayErrorToaster(form.dataset.errorMessage);
                    window.console.error(error);
                }
            });
    };
}

export function domReady() {
    const comments = document.querySelectorAll('[data-comment-id]');
    for (let i = 0, size = comments.length; i < size; ++i) {
        const linkNode = comments[i];
        linkNode.addEventListener('click', () => handleCommentToggle(linkNode));
    }

    const form = document.querySelector('[data-followup-form]');
    if (form) {
        const $form = $(form);
        bindForm($form, {
            category: MA.Context.eventCategory,
            actionPrefix: MA.Context.actionPrefix,
            label: MA.Context.label,
        });

        for (let i = 0, size = form.elements.length; i < size; i++) {
            form.elements[i].addEventListener('change', () => {
                $form.trigger(MaEvent.formStarted);

                sendValues(formToApi(getFormValues(form)));
            });
        }

        const submitButton = form.querySelector('.followup__submit');
        submitButton.addEventListener('click', getSubmitFn(form, submitButton), true);
    }
}
